import { useEffect, useRef, useState } from "react";
import Editor, { OnMount } from "@monaco-editor/react";
import { encodeToBase64 } from "@lxp-app/utils/encodeImperva.ts";
import { cn } from "../../../../../../../utils/cn";

import {
  useExecuteTestCaseMutation,
  useGetSavedCodesQuery,
  useLazyHeartBeatApiQuery,
  useSaveCodeMutation,
  useResetCodingQuestionMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import { useAppDispatch, useAppSelector } from "@assessment/store/store";
import { useAssessStomp } from "@lxp-app/stompAssessment";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { useShowErrors } from "@assessment/pages/assessment-candidate/_components/toasterWrapper";
import { useParams } from "react-router-dom";
import EditorActionComponent from "./editorActionComponent";
import LanguageDropdown from "./languageDropdown";
import EditorComponent from "./editorComponent";

interface Props {
  candidateResponseForQuestion: string | undefined;
  saveCodeInStoreHandler: (val: string | undefined) => void;
  activeQuestionId: string;
  questionData?: any;
  editorTheme: "vs-dark" | "light";
  setEditorTheme: (arg: "vs-dark" | "light") => void;
}

const AssessmentCodeEditor = ({
  saveCodeInStoreHandler,
  activeQuestionId,
  questionData,
  editorTheme,
  setEditorTheme
}: Props) => {
  const { send, output, stompClient, isConnected } = useAssessStomp();
  const dispatch = useAppDispatch();
  const { scheduleId } = useParams();

  const ongoingId = useAppSelector((state) => state.AssessmentSlice.ongoingId);
  const testCases = useAppSelector((state) => state.AssessmentSlice.testCases);
  const testCaseResultHistory = useAppSelector(
    (state) => state.AssessmentSlice.testCaseResultHistory
  );
  const editorFullScreen = useAppSelector(
    (store) => store.AssessmentSlice.editorFullScreen
  );

  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const [editorLanguage, setEditorLanguage] = useState<any>({});
  const [showTestCases, setShowTestCases] = useState(false);
  const [testCaseResult, setTestCaseResult] = useState<any>({});
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [testCaseType, setTestCaseType] = useState<"sample" | "hidden">(
    "sample"
  );
  const [autoSave, setAutoSave] = useState<boolean>(false);
  const [runTimeError, setRunTimeError] = useState<string>("");

  const getResponsiveTestCaseHeight = (containerHeight?: number) => {
    const fallbackHeight =
      typeof window === "undefined" ? 720 : window.innerHeight;
    const resolvedHeight =
      containerHeight && containerHeight > 0 ? containerHeight : fallbackHeight;
    const preferredHeight = Math.round(resolvedHeight * 0.34);
    const maxAllowedHeight = Math.max(180, resolvedHeight - 180);

    return Math.max(180, Math.min(preferredHeight, maxAllowedHeight, 420));
  };

  const [height, setHeight] = useState<number>(getResponsiveTestCaseHeight);
  const [preferredTestCaseHeight, setPreferredTestCaseHeight] =
    useState<number>(getResponsiveTestCaseHeight);
  const [editorObj, setEditorObj] = useState<any>({});
  const [isCodeChange, setIsCodeChange] = useState(false);

  const editorLanguageRef = useRef(editorLanguage);
  const editorValueRef = useRef(editorObj);
  const testCaseTypeRef = useRef(testCaseType);
  const testCasesRef = useRef(testCases);
  const testCaseResultRef = useRef(testCaseResult);
  const questionDataRef = useRef(questionData);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const changeRef = useRef(isCodeChange);
  const loaderStateRef = useRef<
    Record<string, { showLoader: boolean; testCaseType: "sample" | "hidden" }>
  >({});
  const showErrors = useShowErrors();

  const isOnline = useAppSelector((store) => store.AssessmentSlice?.isOnline);

  const constants = useAppSelector((store) => store.AssessmentSlice.duration);

  const starterCodeList = useAppSelector(
    (store) => store.AssessmentSlice.starterCodeList
  );

  const [saveCodeAPi] = useSaveCodeMutation();
  const [executeTestCaseApi] = useExecuteTestCaseMutation();
  const [heartBeatApi] = useLazyHeartBeatApiQuery();
  const [resetCodingQuestionQuery] = useResetCodingQuestionMutation();
  const { data: savedCodesData } = useGetSavedCodesQuery(
    { ongoingId, questionId: questionData?.questionId },
    { refetchOnMountOrArgChange: true }
  );
  const savedCodeList = savedCodesData?.data;

  const errorStatuses = ["RUNTIME_ERROR", "TIMEOUT", "COMPILATION_ERROR"];

  useEffect(() => {
    testCasesRef.current = testCases;
  }, [testCases]);

  useEffect(() => {
    testCaseResultRef.current = testCaseResult;
  }, [testCaseResult]);

  useEffect(() => {
    if (!stompClient || !isConnected || !ongoingId) return;

    const subscription = stompClient.subscribe(
      `/TESTCASE/OUTPUT/${ongoingId}`,
      (message) => {
        if (!message?.body) return;

        const type = testCaseTypeRef.current;
        const cases: any = testCasesRef.current;
        const messageObj = JSON.parse(message.body);
        const { status, testcaseid, compileroutput } = messageObj;

        const isHiddenType = type === "hidden";
        const isHiddenTest = cases?.hiddenTestCases.includes(testcaseid);
        const isSampleTest = cases?.sampleTestCases?.some(
          (test: any) => test?.id === testcaseid
        );

        // Handle runtime error
        if (errorStatuses.includes(status) && testcaseid != null) {
          setRunTimeError(compileroutput);
        }

        // Internal sever error handling
        if (status === "INTERNAL_ERROR" && testcaseid != null) {
          setRunTimeError("Something went wrong. Please try again.");
        }

        // Set test case result
        if (testcaseid != null) {
          const allCases = [
            ...(cases?.sampleTestCases?.map((t: any) => t.id) ?? []),
            ...(cases?.hiddenTestCases ?? [])
          ];
          const allDone = allCases.every((id) => testCaseResultRef.current[id]);
          if (allDone) {
            loaderStateRef.current[messageObj?.questionid] = {
              showLoader: false,
              testCaseType: type
            };
            setShowLoader(false);
          }
          dispatch(
            AssessmentSliceActions.mutateTestCaseResultHistory({
              questionId: messageObj?.questionid,
              testCaseId: testcaseid,
              result: messageObj,
              testCaseType: isSampleTest ? "sample" : "hidden"
            })
          );
          if (
            (isHiddenType && isHiddenTest) ||
            (!isHiddenType && isSampleTest)
          ) {
            setTestCaseResult((prev: Record<string, any>) => ({
              ...prev,
              [testcaseid]: messageObj
            }));
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [stompClient, isConnected, ongoingId]);

  useEffect(() => {
    if (testCaseResultHistory?.[questionData?.questionId]) {
      setTestCaseResult(testCaseResultHistory[questionData?.questionId]);
      const testCaseType = (
        Object.values(testCaseResultHistory?.[questionData?.questionId])[0] as {
          testCaseType?: string;
        }
      )?.testCaseType;
      if (testCaseType === "hidden") {
        setTestCaseType("hidden");
      } else {
        setTestCaseType("sample");
      }
    } else {
      setTestCaseType("sample");
    }
  }, [questionData?.questionId]);

  useEffect(() => {
    editorLanguageRef.current = editorLanguage;
    setRunTimeError("");
    if (testCaseResultHistory?.[questionData?.questionId]) {
      setTestCaseResult(testCaseResultHistory[questionData?.questionId]);
    } else {
      setTestCaseResult({});
    }
  }, [editorLanguage]);

  useEffect(() => {
    editorValueRef.current = editorObj;
  }, [editorObj]);

  useEffect(() => {
    testCaseTypeRef.current = testCaseType;
  }, [testCaseType]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const change = changeRef.current;
      if (change) autoSaveApi();
      // else heartBeatApiCall();
    }, constants?.autoSave * 1000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const container = editorContainerRef.current;

    if (!container || typeof ResizeObserver === "undefined") {
      const fallbackHeight = getResponsiveTestCaseHeight();
      setPreferredTestCaseHeight(fallbackHeight);
      if (showTestCases) {
        setHeight(fallbackHeight);
      }
      return;
    }

    const updateResponsiveHeight = () => {
      const nextHeight = getResponsiveTestCaseHeight(
        container.getBoundingClientRect().height
      );
      setPreferredTestCaseHeight(nextHeight);
      if (showTestCases) {
        setHeight(nextHeight);
      }
    };

    updateResponsiveHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateResponsiveHeight();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [showTestCases]);

  // const heartBeatApiCall = async () => {
  //   try {
  //     if (!scheduleId) {
  //       throw {
  //         status: "",
  //         title: "Schedule Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     if (!ongoingId) {
  //       throw {
  //         status: "",
  //         title: "Ongoing Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     const heartBeatRes = await heartBeatApi({
  //       ongoingId: ongoingId,
  //       scheduleId: scheduleId
  //     });
  //     if (heartBeatRes?.data?.data?.ongoingQPDuration) {
  //       dispatch(
  //         AssessmentSliceActions.mutateTimeLeft(
  //           heartBeatRes?.data?.data?.ongoingQPDuration
  //         )
  //       );
  //     }
  //   } catch (error: any) {
  //     console.log("error", error);
  //   }
  // };

  const autoSaveApi = async () => {
    const lang = editorLanguageRef.current;
    const value = editorValueRef.current;
    const question = questionDataRef.current;
    if (lang?.value != null && value?.[question?.questionId]) {
      try {
        setAutoSave(true);
        const res = await saveCodeAPi({
          ongoingId: ongoingId || "",
          questionId: question?.questionId || "",
          quesVersion: question?.versionnumber,
          body: {
            code: encodeToBase64(value?.[question?.questionId]),
            language: lang?.value
          }
        });
        //Updating the timer
        if (res?.data?.data?.ongoingQpDuration) {
          dispatch(
            AssessmentSliceActions.mutateTimeLeft(
              res?.data?.data?.ongoingQpDuration
            )
          );
        }
        if (res) {
          setIsCodeChange(false);
          dispatch(
            AssessmentSliceActions.mutateCandidateResponse({
              questionId: question?.questionId,
              answerKey: []
            })
          );
        }
        setAutoSave(false);
      } catch (error: any) {
        console.log("Auto-save error", error);
        setAutoSave(false);
      }
    }
  };

  useEffect(() => {
    setEditorObj({});
    questionDataRef.current = questionData;
  }, [questionData]);

  useEffect(() => {
    changeRef.current = isCodeChange;
  }, [isCodeChange]);

  const handleSave = async (isExecute = false, hidden: boolean) => {
    if (!isOnline) {
      return;
    }
    const qId = questionData?.questionId;
    setShowLoader(true);
    loaderStateRef.current[qId] = {
      showLoader: true,
      testCaseType: hidden ? "hidden" : "sample"
    };
    dispatch(
      AssessmentSliceActions.mutateTestCaseResultHistory({
        questionId: questionData?.questionId,
        testCaseId: "",
        result: {},
        testCaseType: ""
      })
    );
    setTestCaseResult({});
    setAutoSave(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    try {
      const res: any = await saveCodeAPi({
        ongoingId: ongoingId || "",
        questionId: questionData?.questionId || "",
        quesVersion: questionData?.versionnumber,
        body: {
          code: encodeToBase64(editorObj?.[questionData?.questionId]),
          language: editorLanguage?.value
        }
      });

      //Updating the timer
      if (res?.data?.data?.ongoingQpDuration) {
        dispatch(
          AssessmentSliceActions.mutateTimeLeft(
            res?.data?.data?.ongoingQpDuration
          )
        );
      }
      if (res) {
        dispatch(
          AssessmentSliceActions.mutateCandidateResponse({
            questionId: questionData?.questionId,
            answerKey: []
          })
        );
        setIsCodeChange(false);
      }
      setAutoSave(false);
      if (isExecute && res?.data?.data?.id) {
        const res2: any = await executeTestCaseApi({
          ongoingId: ongoingId || "",
          body: {
            questionId: questionData?.questionId || "",
            questionVersion: questionData?.versionnumber,
            savedCodeId: res?.data?.data?.id,
            hidden: hidden
          }
        });
        if (res2) {
          if (ongoingId && questionData?.questionId) {
            if (hidden)
              dispatch(
                AssessmentSliceActions.mutateQuestionProgress({
                  questionId: questionData?.questionId,
                  sectionId: questionData?.sectionId,
                  status: "completed"
                })
              );
            send(ongoingId, questionData?.questionId);
          }
        }
      }
      timeoutRef.current = setTimeout(() => {
        setRunTimeError("");
        if (Object.keys(testCaseResultRef.current)?.length == 0) {
          setShowLoader(false);
          if (showErrors)
            showErrors(
              "Request Timeout",
              [],
              "error",
              "The testcase took too long to respond. Please try again."
            );
        }
      }, 90000);
    } catch (error) {
      setShowLoader(false);
      setAutoSave(false);
      console.log("error", error);
    }
  };

  useEffect(() => {
    const qId = questionData?.questionId;
    const savedState = loaderStateRef.current[qId];

    if (savedState) {
      setShowLoader(false);
      setTestCaseType(savedState.testCaseType);
    } else {
      setShowLoader(false);
    }
    if (testCaseResultHistory?.[qId]) {
      setTestCaseResult(testCaseResultHistory[qId]);
    } else {
      setTestCaseResult({});
    }

    setRunTimeError("");
  }, [questionData?.questionId]);

  const runTestCases = () => {
    if (!showLoader) {
      setTestCaseType("sample");
      handleSave(true, false);
      if (!showTestCases) {
        setShowTestCases(true);
        setHeight(preferredTestCaseHeight);
      }
    }
  };

  const submitAnswers = () => {
    if (!showLoader) {
      setTestCaseType("hidden");
      handleSave(true, true);
      saveCodeInStoreHandler(editorObj?.[questionData?.questionId]);
      if (!showTestCases) {
        setShowTestCases(true);
        setHeight(preferredTestCaseHeight);
      }
    }
  };

  useEffect(() => {
    const questionId = questionDataRef.current?.questionId;
    const code = editorObj?.[questionId];
    if (questionId && code) {
      dispatch(
        AssessmentSliceActions.mutateLatestCodingState({
          questionId,
          code,
          language: editorLanguage?.value,
          version: questionDataRef.current?.versionnumber
        })
      );
    }
  }, [editorObj, editorLanguage]);

  useEffect(() => {
    return () => {
      dispatch(AssessmentSliceActions.mutateLatestCodingState(null));
    };
  }, []);
  const resetToStarterCode = async () => {
    try {
      if (questionData?.startercode?.length > 0) {
        const temp = questionData.startercode.filter(
          (el: any) => el?.language === editorLanguage?.value
        );

        if (temp?.[0]?.code) {
          setEditorObj({ [questionData.questionId]: temp[0].code });
          if (savedCodeList?.[0]?.id) {
            const res = await resetCodingQuestionQuery({
              questionId: questionData?.questionId || "",
              questionVersion: questionData?.versionnumber,
              latestCodeId: savedCodeList?.[0]?.id,
              ongoingId: ongoingId || ""
            });
            if (res?.data?.data?.message) {
              dispatch(
                AssessmentSliceActions.mutateQuestionProgress({
                  questionId: questionData.questionId,
                  sectionId: questionData?.sectionId,
                  status: "unanswered"
                })
              );
            }
          }
        } else if (starterCodeList?.length > 0) {
          const starterTemp = starterCodeList.filter(
            (el: any) => el?.language === editorLanguage?.value
          );

          if (starterTemp?.[0]?.code) {
            setEditorObj({ [questionData.questionId]: starterTemp[0].code });
          }
        }
      } else if (starterCodeList?.length > 0) {
        const temp = starterCodeList.filter(
          (el: any) => el?.language === editorLanguage?.value
        );

        if (temp?.[0]?.code) {
          setEditorObj({ [questionData.questionId]: temp[0].code });
        }
      }
    } catch (error) {
      console.error("Error resetting starter code:", error);
    }
  };

  return (
    <div
      ref={editorContainerRef}
      data-tour="acq-editor"
      className={cn(
        "flex h-full min-h-0 w-full flex-1 flex-col pt-2 rounded-r-2xl",
        editorTheme === "vs-dark" && "bg-[#1E1E1E]",
        editorTheme === "light" &&
          "bg-white border border-gray-200 dark:border-slate-400",
        editorFullScreen && "lg:w-full"
      )}
    >
      {/* Top */}
      <LanguageDropdown
        questionData={questionData}
        editorLanguage={editorLanguage}
        editorTheme={editorTheme}
        setEditorLanguage={setEditorLanguage}
        showLoader={showLoader}
        runTestCases={runTestCases}
        submitAnswers={submitAnswers}
        setEditorTheme={setEditorTheme}
        handleSave={autoSaveApi}
        isCodeChange={isCodeChange}
        testCaseType={testCaseType}
      />

      <div className="flex min-h-[40dvh] flex-col flex-1 overflow-hidden sm:min-h-[45dvh] lg:min-h-0">
        <EditorComponent
          autoSaveApi={autoSaveApi}
          questionData={questionData}
          editorObj={editorObj}
          editorTheme={editorTheme}
          showTestCases={showTestCases}
          editorLanguage={editorLanguage}
          savedCodeList={savedCodeList}
          setIsCodeChange={setIsCodeChange}
          setEditorObj={setEditorObj}
          resetToStarterCode={resetToStarterCode}
        />
      </div>

      <EditorActionComponent
        editorTheme={editorTheme}
        runTestCases={runTestCases}
        submitAnswers={submitAnswers}
        showLoader={showLoader}
        testCaseType={testCaseType}
        showTestCases={showTestCases}
        setShowTestCases={setShowTestCases}
        preferredTestCaseHeight={preferredTestCaseHeight}
        height={height}
        setHeight={setHeight}
        setShowLoader={setShowLoader}
        testCaseResult={testCaseResult}
        runTimeError={runTimeError}
        autoSave={autoSave}
        resetToStarterCode={resetToStarterCode}
      />
    </div>
  );
};

export default AssessmentCodeEditor;
